import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { Preferences } from '@/types/deepadmin/Preferences';
import deepadminApi from '@/api/deepadmin/deepadmin';
import {
  getPreferences,
  updatePreferences
} from '@/api/deepadmin/users-me-profile.ts';

export const usePreferencesStore = defineStore('preferences', () => {
  // End point url
  const endPointUrl = '/users/me/profile/';

  const preferences = ref<Preferences | undefined>();

  const fetchAllPending = ref(false);
  const setPreferencePending = ref(false);
  const removePreferencePending = ref(false);

  async function fetchAll() {
    try {
      fetchAllPending.value = true;
      const response = await getPreferences();
      preferences.value = response.data?.preferences;
      fetchAllPending.value = false;
      return Promise.resolve(response);
    } catch (error) {
      fetchAllPending.value = false;
      return Promise.reject(error);
    }
  }

  async function setPreference(
    key: string,
    value: string | number | null | boolean | unknown[] | undefined
  ) {
    try {
      setPreferencePending.value = true;
      const body = { preferences: {} };
      body.preferences[`${key}`] = value;
      const response = await deepadminApi.patch(endPointUrl, body);
      preferences.value = response.data?.preferences;
      setPreferencePending.value = false;
      return Promise.resolve(response);
    } catch (error) {
      setPreferencePending.value = false;
      return Promise.reject(error);
    }
  }

  // Clears several keys in a single PATCH: the endpoint merges, so listing the
  // keys as null deletes exactly those and leaves the rest of the payload
  // alone. Syncs local state from the response, so callers don't have to
  // refetch to see the wipe.
  async function removePreferences(keyNames: string[]) {
    try {
      removePreferencePending.value = true;
      const body = {
        preferences: Object.fromEntries(keyNames.map((key) => [key, null]))
      };
      const response = await updatePreferences(body);
      preferences.value = response.data?.preferences;
      removePreferencePending.value = false;
      return Promise.resolve(response);
    } catch (error) {
      removePreferencePending.value = false;
      return Promise.reject(error);
    }
  }

  return {
    preferences,
    fetchAllPending,
    setPreferencePending,
    removePreferencePending,
    fetchAll,
    setPreference,
    removePreferences
  };
});
